import { useState } from "react";
import useSWR from "swr";
import { Bookmark, Sparkles, Star, Workflow } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Card, CardBody, CardHeader, CardTitle } from "@/components/ui/card";
import { Switch } from "@/components/ui/switch";
import { usePreferences } from "@/hooks/usePreferences";
import { swrFetcher, type ModelInfo } from "@/lib/api";

export function GeneralSettingsPage() {
  const { preferences, isLoading, update } = usePreferences();
  const { data: models = [] } = useSWR<ModelInfo[]>("/api/capabilities", swrFetcher);
  const [saving, setSaving] = useState(false);
  const [message, setMessage] = useState<string | null>(null);
  const [messageIsError, setMessageIsError] = useState(false);

  const modelName = (key: string | null | undefined) =>
    models.find((model) => model.key === key)?.display_name ?? key ?? "Not set";

  async function save(patch: Parameters<typeof update>[0], success: string) {
    setSaving(true);
    setMessage(null);
    setMessageIsError(false);
    try {
      await update(patch);
      setMessage(success);
    } catch (error) {
      setMessageIsError(true);
      setMessage(error instanceof Error ? error.message : "Settings could not be saved.");
    } finally {
      setSaving(false);
    }
  }

  return (
    <div className="max-w-4xl space-y-5">
      <div className="mb-6">
        <p className="text-xs font-semibold uppercase tracking-[0.14em] text-studio-accent">Personal</p>
        <h2 className="mt-2 text-xl font-semibold tracking-tight text-studio-ink">General settings</h2>
        <p className="mt-1.5 text-sm leading-6 text-studio-muted">
          Choose how Studio prepares model controls and restores your preferred workflow.
        </p>
      </div>

      <Card>
        <CardHeader>
          <div className="flex items-center gap-3">
            <span className="flex h-9 w-9 items-center justify-center rounded-lg bg-studio-accent-soft text-studio-accent">
              <Sparkles className="h-4 w-4" />
            </span>
            <div>
              <CardTitle>Prompt enhancement</CardTitle>
              <p className="mt-1 text-xs text-studio-muted">Choose whether supported BytePlus workflows should enhance before generation.</p>
            </div>
          </div>
        </CardHeader>
        <CardBody>
          <div className="flex flex-col items-stretch justify-between gap-3 sm:flex-row sm:items-start sm:gap-6">
            <div>
              <label htmlFor="enhance-before-generation" className="text-sm font-semibold text-studio-ink">
                Enhance before generation
              </label>
              <p className="mt-1 max-w-xl text-xs leading-5 text-studio-muted">
                The primary action becomes Enhance &amp; review. Generation remains blocked until you apply the revision and confirm its generation cost.
              </p>
            </div>
            <Switch
              id="enhance-before-generation"
              checked={preferences?.enhance_before_generation ?? false}
              disabled={isLoading || saving}
              onCheckedChange={(checked) =>
                void save({ enhance_before_generation: checked }, "Prompt enhancement preference updated.")
              }
            />
          </div>
        </CardBody>
      </Card>

      <div className="grid gap-5 md:grid-cols-2">
        <Card>
          <CardHeader>
            <div className="flex items-center gap-3">
              <Star className="h-4 w-4 text-studio-accent" />
              <CardTitle>Favorite model</CardTitle>
            </div>
          </CardHeader>
          <CardBody>
            <p className="text-sm font-semibold text-studio-ink">{modelName(preferences?.favorite_model_key)}</p>
            <p className="mt-1 text-xs leading-5 text-studio-muted">
              Mark a model from the Studio model picker. It becomes your fallback when no default workflow is set.
            </p>
            {preferences?.favorite_model_key && (
              <Button
                className="mt-4"
                variant="outline"
                size="sm"
                loading={saving}
                onClick={() => void save({ favorite_model_key: null }, "Favorite model cleared.")}
              >
                Clear favorite
              </Button>
            )}
          </CardBody>
        </Card>

        <Card>
          <CardHeader>
            <div className="flex items-center gap-3">
              <Workflow className="h-4 w-4 text-studio-accent" />
              <CardTitle>Default workflow</CardTitle>
            </div>
          </CardHeader>
          <CardBody>
            {preferences?.default_workflow ? (
              <>
                <p className="text-sm font-semibold text-studio-ink">
                  {modelName(preferences.default_workflow.model_key)}
                </p>
                <p className="mt-1 text-xs capitalize text-studio-muted">
                  {preferences.default_workflow.modality} · {preferences.default_workflow.capability_key.replace(/_/g, " ")}
                </p>
                <Button
                  className="mt-4"
                  variant="outline"
                  size="sm"
                  loading={saving}
                  onClick={() => void save({ default_workflow: null }, "Default workflow cleared.")}
                >
                  Clear default
                </Button>
              </>
            ) : (
              <div className="flex items-start gap-3 text-studio-muted">
                <Bookmark className="mt-0.5 h-4 w-4 shrink-0" />
                <p className="text-xs leading-5">Open Studio, choose a model and capability, then select “Set as default”.</p>
              </div>
            )}
          </CardBody>
        </Card>
      </div>

      {message && (
        <p
          role={messageIsError ? "alert" : "status"}
          className={messageIsError ? "text-xs font-medium text-studio-danger" : "text-xs font-medium text-studio-success"}
        >
          {message}
        </p>
      )}
    </div>
  );
}
